import { TypedDocumentNode, gql } from '@apollo/client';
import { defineMessages } from 'react-intl';

import Switch from 'atoms/inputs/Switch';
import { useCurrentUserContext } from 'contexts/currentUser';
import useMutation from 'hooks/graphql/useMutation';

import SettingsSection from '../SettingsSection';
import {
  UpdatePermanentProTutorialMutation,
  UpdatePermanentProTutorialMutationVariables,
} from './__generated__/index.graphql';

const PERMANENT_PRO_TUTORIAL_MUTATION = gql`
  mutation UpdatePermanentProTutorialMutation(
    $input: updateUserSettingsInput!
  ) {
    updateUserSettings(input: $input) {
      userSettings {
        id
        hidePermanentTutorials
      }
      errors {
        path
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  UpdatePermanentProTutorialMutation,
  UpdatePermanentProTutorialMutationVariables
>;

const useTogglePermanentProTutorial = () => {
  const { currentUser } = useCurrentUserContext();
  const [mutate] = useMutation(PERMANENT_PRO_TUTORIAL_MUTATION, {
    showErrorsWithSnackNotification: true,
  });

  return async () => {
    mutate({
      variables: {
        input: {
          hidePermanentTutorials:
            !currentUser?.userSettings?.hidePermanentTutorials,
        },
      },
      optimisticResponse: {
        updateUserSettings: {
          __typename: 'updateUserSettingsPayload',
          userSettings: {
            __typename: 'UserSettings',
            id: currentUser!.userSettings.id,
            hidePermanentTutorials:
              !currentUser?.userSettings?.hidePermanentTutorials,
          },
          errors: [],
        },
      },
    });
  };
};

const messages = defineMessages({
  title: {
    id: 'Settings.enablePermanentProTutorial.title',
    defaultMessage: 'Keep Basic Tutorial open',
  },
  description: {
    id: 'Settings.enablePermanentProTutorial.description',
    defaultMessage:
      'Keep Pro Basic Tutorial open even once you have completed them',
  },
});

const EnablePermanentProTutorial = () => {
  const { currentUser } = useCurrentUserContext();
  const toggle = useTogglePermanentProTutorial();

  if (!currentUser) return null;

  return (
    <SettingsSection
      title={messages.title}
      description={messages.description}
      toggleButton={
        <Switch
          checked={!currentUser?.userSettings?.hidePermanentTutorials}
          onChange={() => {
            toggle();
          }}
        />
      }
    />
  );
};

export default EnablePermanentProTutorial;
